// src/pages/Orders/OrdersOnMap.jsx
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useOrderDetailsFilterQuery, useOrderStatusesQuery } from "@queries/order";
import { useTimeSlotsQuery } from "@queries/time_slots";
import { loadGoogleMaps } from "@lib/googleMapsLoader";
import "@styles/opencart-admin.css";

const TYPES = [
  "express",
  "housekeeping",
  "carpets",
  "car_cleaning",
  "normal",
  "skip",
  "complementary",
  "disinfection",
  "furniture",
  "chemicals",
];

const AMMAN = { lat: 31.9539, lng: 35.9106 };

const getStatusId = (r) => Number(r?.order_status_id ?? r?.order?.order_status_id);

const isJordanLat = (v) => v >= 29 && v <= 33.8;
const isJordanLng = (v) => v >= 34.5 && v <= 39.5;
const isInJordan = (p) => isJordanLat(p.lat) && isJordanLng(p.lng);

const parseCsv = (v) =>
  String(v || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const fromSearch = (sp) => {
  const orderTypeParam = sp.get("order_type");
  return {
    day: sp.get("day") || new Date().toISOString().slice(0, 10),
    time_slot: sp.get("time_slot") || "",
    order_status_id: sp.get("order_status_id") || "",
    order_type: orderTypeParam !== null ? parseCsv(orderTypeParam) : ["normal", "skip", "express", "carpets"],
    page: Math.max(1, parseInt(sp.get("page") || "1", 10)),
    page_size: Math.max(1, parseInt(sp.get("page_size") || "100", 10)),
  };
};

const toSearch = (f) => {
  const sp = new URLSearchParams();
  if (f.day) sp.set("day", f.day);
  if (f.time_slot) sp.set("time_slot", f.time_slot);
  if (f.order_status_id) sp.set("order_status_id", f.order_status_id);
  if (Array.isArray(f.order_type) && f.order_type.length) sp.set("order_type", f.order_type.join(","));

  sp.set("page", String(f.page || 1));
  sp.set("page_size", String(f.page_size || 100));
  return sp;
};

const STATUS_COLOR = {
  2: "#1e90ff",
  5: "#8e44ad",
  17: "#2ecc71",
  18: "#e67e22",
};

function markerIconForOrder(row) {
  const id = Number(row?.order_status_id ?? row?.order?.order_status_id);
  const color = STATUS_COLOR[id] || "#e74c3c";

  return {
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 2.5,
    anchor: new window.google.maps.Point(12, 24),
  };
}

export default function OrdersOnMap() {
  const [search, setSearch] = useSearchParams();
  const [filters, setFilters] = React.useState(fromSearch(search));
  React.useEffect(() => setFilters(fromSearch(search)), [search]);

  const { data: timeSlots = [], isLoading: tsLoading } = useTimeSlotsQuery(filters.day);

  const queryFilters = React.useMemo(
    () => ({
      from_date: filters.day || undefined,
      to_date: filters.day || undefined,
      time_slot: filters.time_slot || undefined,
      order_status_id: filters.order_status_id || undefined,
      order_type: filters.order_type?.length ? filters.order_type : undefined,
      page: filters.page || 1,
      page_size: filters.page_size || 100,
    }),
    [filters]
  );

  const { data: resp, isLoading, isFetching } = useOrderDetailsFilterQuery(queryFilters);
  const apiRows = resp?.data ?? [];

  const rows = React.useMemo(() => {
    let out = apiRows;

    // order type multi-filter
    if (filters.order_type?.length) {
      const set = new Set(filters.order_type);
      out = out.filter((r) => set.has(String(r.type || "").trim()));
    }

    return out;
  }, [apiRows, filters.order_type]);

  const meta = resp?.meta ?? { page: filters.page, page_size: filters.page_size, total: rows.length, pages: 1 };

  const { data: statuses = [] } = useOrderStatusesQuery();

  const apply = () => setSearch(toSearch({ ...filters, page: 1 }), { replace: true });
  const setPage = (p) => setSearch(toSearch({ ...filters, page: p }), { replace: true });

  const [gmReady, setGmReady] = React.useState(false);
  React.useEffect(() => {
    let ok = true;
    loadGoogleMaps().then(() => ok && setGmReady(true));
    return () => {
      ok = false;
    };
  }, []);

  const toggleOrderType = (type, checked) => {
    setFilters((f) => {
      const current = f.order_type || [];
      if (checked) {
        return { ...f, order_type: [...current, type] };
      } else {
        return { ...f, order_type: current.filter((t) => t !== type) };
      }
    });
  };

  const clearOrderType = () => setFilters((f) => ({ ...f, order_type: [] }));

  const failedRows = React.useMemo(() => rows.filter((r) => !getOrderCoords(r)), [rows]);

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <div className="oc-card">Loading…</div>}

      <div className="oc-header">
        <h1>Orders Map</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Orders by Date</span>
        </nav>
      </div>

      <div className="oc-card oc-filter">
        <div className="title">Filter</div>

        {/* Improved flex layout for clearer filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <div>
            <label className="lbl">Delivery/Pickup Day</label>
            <input
              type="date"
              value={filters.day}
              onChange={(e) => setFilters((f) => ({ ...f, day: e.target.value }))}
              className="oc-input"
              style={{ width: "200px" }}
            />
          </div>

          <div>
            <label className="lbl">Time Slot</label>
            <select
              value={filters.time_slot}
              onChange={(e) => setFilters((f) => ({ ...f, time_slot: e.target.value }))}
              className="oc-input"
              disabled={tsLoading}
              style={{ width: "200px" }}
            >
              <option value="">All</option>
              {timeSlots.map((t) => {
                const value = t?.value ?? t;
                const label = t?.label ?? t;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="lbl">Status</label>
            <select
              value={filters.order_status_id}
              onChange={(e) => setFilters((f) => ({ ...f, order_status_id: e.target.value }))}
              className="oc-input"
              style={{ width: "200px" }}
            >
              <option value="">All</option>
              {statuses.map((s) => (
                <option key={s.order_status_id} value={s.order_status_id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button className="oc-btn" onClick={apply}>
              Filter
            </button>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <label className="lbl" style={{ marginBottom: 0 }}>Order Types</label>
            <button
              className="oc-btn ghost"
              style={{ padding: "4px 8px", fontSize: 12, height: "auto", minHeight: "auto" }}
              type="button"
              onClick={clearOrderType}
              disabled={!filters.order_type?.length}
            >
              Clear All
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {TYPES.map((t) => {
              const isSelected = filters.order_type?.includes(t);
              return (
                <div
                  key={t}
                  onClick={() => toggleOrderType(t, !isSelected)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: isSelected ? "1px solid #1e90ff" : "1px solid #ddd",
                    backgroundColor: isSelected ? "#e6f2ff" : "#f9f9f9",
                    color: isSelected ? "#1e90ff" : "#555",
                    cursor: "pointer",
                    fontSize: "13px",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  {labelForType(t)}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Single map only */}
      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <strong>
              Orders Map • Date:{filters.day || "-"} • Orders count:{rows.length}
              {filters.order_type?.length ? ` • Types: ${filters.order_type.map(labelForType).join(", ")}` : ""}
            </strong>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {Object.entries(STATUS_COLOR).map(([sid, color]) => (
              <span key={sid} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: color }} />
                {statuses.find(s => Number(s.order_status_id) === Number(sid))?.name || `Status ${sid}`}
              </span>
            ))}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: "#e74c3c" }} />
              Other
            </span>
          </div>
        </div>

        {!gmReady && (
          <div style={{ height: 560, display: "grid", placeItems: "center", background: "#f5f5f5" }}>Loading map…</div>
        )}

        {gmReady && <OrdersMap rows={rows} height={560} />}
      </div>

      {failedRows.length > 0 && (
        <div className="oc-card" style={{ marginTop: 16 }}>
          <h3 style={{ color: "#e74c3c", marginTop: 0, fontSize: "16px" }}>
            Orders missing map coordinates ({failedRows.length})
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {failedRows.map((r) => (
              <a
                key={r.order_id}
                href={`/orders/${r.order_id}`}
                target="_blank"
                rel="noreferrer"
                className="oc-btn ghost"
                style={{ padding: "6px 12px", fontSize: 13, height: "auto", minHeight: "auto", border: "1px solid #ddd" }}
              >
                Order #{r.order_id} ({r.customer || r.name || "-"})
              </a>
            ))}
          </div>
        </div>
      )}

      {!isLoading && rows.length === 0 && <div className="oc-card">No results</div>}

      <div className="oc-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
        <div>
          Showing page <strong>{meta.page}</strong> of <strong>{meta.pages}</strong> ({meta.total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={meta.page <= 1} onClick={() => setPage(1)}>
            « First
          </button>
          <button className="oc-btn ghost" disabled={meta.page <= 1} onClick={() => setPage(meta.page - 1)}>
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={meta.page >= meta.pages} onClick={() => setPage(meta.page + 1)}>
            Next ›
          </button>
          <button className="oc-btn ghost" disabled={meta.page >= meta.pages} onClick={() => setPage(meta.pages)}>
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

function OrdersMap({ rows, height = 520 }) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const infoRef = React.useRef(null);

  React.useEffect(() => {
    const g = window.google;
    if (!g?.maps || !ref.current) return;

    if (!mapRef.current) {
      mapRef.current = new g.maps.Map(ref.current, {
        center: AMMAN,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
      });
      infoRef.current = new g.maps.InfoWindow();
    }

    const map = mapRef.current;
    const info = infoRef.current;

    for (const m of markersRef.current) m.setMap(null);
    markersRef.current = [];

    const points = rows
      .map((r) => ({ row: r, pos: getOrderCoords(r) }))
      .filter((x) => x.pos && Number.isFinite(x.pos.lat) && Number.isFinite(x.pos.lng));

    if (!points.length) {
      map.setCenter(AMMAN);
      map.setZoom(12);
      return;
    }

    const jordanPoints = points.filter((p) => isInJordan(p.pos));

    for (const { row, pos } of points) {
      const marker = new window.google.maps.Marker({
        position: pos,
        map,
        title: `Order ${row.order_id}`,
        icon: markerIconForOrder(row),
      });

      marker.addListener("click", () => {
        info.setContent(renderInfoHtml(row));
        info.open({ map, anchor: marker });
      });

      markersRef.current.push(marker);
    }

    if (!jordanPoints.length) {
      map.setCenter(AMMAN);
      map.setZoom(12);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    for (const { pos } of jordanPoints) bounds.extend(pos);

    map.fitBounds(bounds);

    const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      if (typeof z === "number" && z > 16) map.setZoom(16);
      if (typeof z === "number" && z < 11) map.setZoom(12);
    });

    return () => {
      if (listener) window.google.maps.event.removeListener(listener);
    };
  }, [rows]);

  return <div style={{ width: "100%", height }} ref={ref} />;
}

function getOrderCoords(r) {
  const candidates = [
    { lat: r.pickup_lat, lng: r.pickup_lng },
    { lat: r.dropoff_lat, lng: r.dropoff_lng },
    { lat: r.lat, lng: r.lng },
    { lat: r.pickup_latitude, lng: r.pickup_longitude },
    { lat: r.dropoff_latitude, lng: r.dropoff_longitude },
  ];

  for (const c of candidates) {
    const lat = Number(c?.lat);
    const lng = Number(c?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) > 0.0001 && Math.abs(lng) > 0.0001) {
      return { lat, lng };
    }
  }

  // Try to decode MySQL WKB binary format
  const wkb = r?.order?.pickup_latlong || r?.order?.dropoff_latlong;
  const decoded = decodeMysqlPointWkb(wkb);
  if (decoded) return decoded;

  // Try to parse Plus Code from pickup_area or pickup_address
  const plusCode = r?.pickup_area || r?.pickup_address;
  if (plusCode && window.google?.maps?.LatLng) {
    try {
      // Use Google Maps Geocoder to resolve Plus Code
      const geocoder = new window.google.maps.Geocoder();
      let found = null;
      geocoder.geocode({ address: plusCode }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          found = { lat: loc.lat(), lng: loc.lng() };
        }
      });
      if (found) return found;
    } catch (e) {}
  }

  // Fallback: show at Amman center with a warning (could be improved to show a special marker)
  return { ...AMMAN, _fallback: true };
}

function decodeMysqlPointWkb(value) {
  if (!value) return null;

  const bytes = new Uint8Array([...String(value)].map((ch) => ch.charCodeAt(0) & 0xff));
  const attempts = [0, 4];
  const results = [];

  for (const offset of attempts) {
    if (bytes.length < offset + 1 + 4 + 8 + 8) continue;

    const dv = new DataView(bytes.buffer, bytes.byteOffset + offset, bytes.byteLength - offset);
    const little = dv.getUint8(0) === 1;

    const type = dv.getUint32(1, little);
    if (type !== 1) continue;

    const x = dv.getFloat64(5, little);
    const y = dv.getFloat64(13, little);

    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    const check = (a, b) => {
      if (isJordanLat(a) && isJordanLng(b)) return { lat: a, lng: b, jordan: true };
      if (isJordanLat(b) && isJordanLng(a)) return { lat: b, lng: a, jordan: true };

      const isLat = (v) => v >= -90 && v <= 90;
      const isLng = (v) => v >= -180 && v <= 180;

      if (isLat(a) && isLng(b)) return { lat: a, lng: b, jordan: false };
      if (isLat(b) && isLng(a)) return { lat: b, lng: a, jordan: false };

      return null;
    };

    const parsed = check(y, x) || check(x, y);
    if (parsed) results.push(parsed);
  }

  if (results.length === 0) return null;

  const jordan = results.find((r) => r.jordan);
  if (jordan) {
    delete jordan.jordan;
    return jordan;
  }

  const nonZero = results.find((r) => Math.abs(r.lat) > 0.0001 || Math.abs(r.lng) > 0.0001);
  if (nonZero) {
    delete nonZero.jordan;
    return nonZero;
  }

  delete results[0].jordan;
  return results[0];
}

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderInfoHtml(r) {
  const id = esc(r.order_id);
  const ts = esc(r.next_ts || r.time_slot || r.timeslot || "-");
  const status = esc(r.status_text || r.status || "-");
  const customer = esc(r.customer || r.name || "-");
  const area = esc(r.area || r.payment_area || "-");
  const payment = esc(r.payment || r.payment_method || "-");
  const total = Number.isFinite(r.order_total) ? `${Number(r.order_total).toFixed(2)} JOD` : "-";
  const type = esc(labelForType(String(r.type || "-")));

  return `
    <div style="min-width:260px;max-width:340px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid #eee">
        <div><strong>DID: ${id}</strong></div>
        <div style="font-size:14px;font-weight:bold;color:#e74c3c;background:#fdf0ed;padding:2px 6px;border-radius:4px;">${ts}</div>
      </div>
      <div style="font-size:12px;line-height:1.45">
        <div><strong>Status:</strong> ${status}</div>
        <div><strong>Type:</strong> ${type}</div>
        <div><strong>Customer:</strong> ${customer}</div>
        <div><strong>Area:</strong> ${area}</div>
        <div><strong>Payment:</strong> ${payment}</div>
        <div><strong>Total:</strong> ${esc(total)}</div>
        <div style="margin-top:8px">
          <a href="/orders/${id}" target="_blank" rel="noreferrer" style="display:inline-block;padding:4px 8px;background:#3498db;color:#fff;text-decoration:none;border-radius:4px;">Open order</a>
        </div>
      </div>
    </div>
  `;
}

function labelForType(t) {
  if (t === "car_cleaning") return "Car Cleaning";
  if (t === "housekeeping") return "Housekeeping";
  if (t === "express") return "Express";
  if (t === "carpets") return "Carpets";
  if (t === "complementary") return "Complementary";
  if (t === "disinfection") return "Disinfection";
  if (t === "furniture") return "Furniture";
  if (t === "chemicals") return "Chemicals";
  if (!t) return "-";
  return t.charAt(0).toUpperCase() + t.slice(1);
}
